import { ChevronLeft, Globe, Award, FlaskConical, Microscope, Heart, Users, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TAGNPAY_LOGO = "https://customer-assets.emergentagent.com/job_images-34/artifacts/tdji2o49_tagnpay-logo-new.png";
const ABOUT_HERO = "https://images.unsplash.com/photo-1579154204601-01588f351e67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxob3NwaXRhbCUyMGxhYm9yYXRvcnklMjBtb2Rlcm58ZW58MHx8fHwxNzc2MjQzMTk3fDA&ixlib=rb-4.1.0&q=85";
const LAB_IMAGE = "https://images.unsplash.com/photo-1631816287686-a53a91445e44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxsYWJvcmF0b3J5JTIwc2NpZW50aXN0JTIwbWljcm9zY29wZSUyMGRpYWdub3N0aWNzfGVufDB8fHx8MTc3NjI0MzE3Mnww&ixlib=rb-4.1.0&q=85";
const PRODUCT_IMAGE = "https://images.unsplash.com/photo-1655210913810-33acfa96d1e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxob3NwaXRhbCUyMGxhYm9yYXRvcnklMjBtb2Rlcm58ZW58MHx8fHwxNzc2MjQzMTk3fDA&ixlib=rb-4.1.0&q=85";

const stats = [
  { label: "Countries Served", value: "100+", icon: Globe, color: "text-[#008A99]", bg: "bg-[#E0F4F5]" },
  { label: "Tests Enabled", value: "150Cr+", icon: FlaskConical, color: "text-emerald-600", bg: "bg-emerald-50" },
  { label: "People Served", value: "200M+", icon: Heart, color: "text-rose-500", bg: "bg-rose-50" },
  { label: "Awards Won", value: "50+", icon: Award, color: "text-amber-600", bg: "bg-amber-50" },
];

const divisions = [
  { name: "Clinical Chemistry", desc: "Fully automated analyzers for routine biochemistry testing" },
  { name: "Hematology", desc: "Advanced 3-part and 5-part differential blood cell counters" },
  { name: "Urinalysis", desc: "Semi-automated and automated urine analysis systems" },
  { name: "Diabetes Management", desc: "HbA1c testing systems for glycemic control monitoring" },
  { name: "Immunology", desc: "ELISA processors for infectious disease and hormone testing" },
  { name: "Molecular Diagnostics", desc: "PCR-based platforms for precision medicine" },
];

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-8 animate-slide-up" data-testid="about-page">
      {/* Back button */}
      <button onClick={() => navigate("/app")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#008A99] transition-colors font-medium" data-testid="back-to-home">
        <ChevronLeft className="w-4 h-4" /> Back to Home
      </button>

      {/* Hero */}
      <div className="relative h-48 sm:h-56 rounded-2xl overflow-hidden" data-testid="about-hero">
        <img src={ABOUT_HERO} alt="TransAsia Laboratory" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <p className="text-xs font-bold text-white/50 uppercase tracking-[0.2em]">About</p>
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight mt-1" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
            TransAsia Bio-Medicals
          </h1>
          <p className="text-sm text-white/70 mt-1">Part of the Erba-Transasia Group</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3" data-testid="about-stats">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl border border-gray-200/80 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
            <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center mb-3`}>
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <p className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{stat.value}</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Mission */}
      <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-sm" data-testid="about-mission">
        <p className="text-xs font-bold text-[#008A99] uppercase tracking-[0.15em] mb-3">Our Mission</p>
        <p className="text-gray-700 leading-relaxed text-sm">
          TransAsia Bio-Medicals is a global IVD company focused on creating social impact in developing nations by delivering
          <strong className="text-gray-900"> innovative, affordable, and sustainable healthcare solutions</strong>. As India's
          leading In-Vitro Diagnostics company, we provide a full line of diagnostic testing across Clinical Chemistry, Hematology,
          Urinalysis, Diabetes Management, Immunology, and Molecular Diagnostics.
        </p>
      </div>

      {/* Lab Image + Global Reach */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="relative h-48 sm:h-full rounded-2xl overflow-hidden" data-testid="about-lab-image">
          <img src={LAB_IMAGE} alt="Laboratory Scientist" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Global Operations</p>
            <p className="text-sm text-white font-semibold mt-0.5">R&D across 4 countries</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-sm" data-testid="about-global">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-[0.15em] mb-3">Global Presence</p>
          <div className="space-y-3">
            {[
              { region: "Manufacturing", locations: "India, Czech Republic, USA" },
              { region: "R&D Centers", locations: "France, UK, India, Czech Republic" },
              { region: "Serving", locations: "100+ Countries Worldwide" },
            ].map((item) => (
              <div key={item.region} className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#008A99] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold text-gray-900">{item.region}</p>
                  <p className="text-xs text-gray-500">{item.locations}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Product Divisions */}
      <div data-testid="about-divisions">
        <p className="text-xs font-bold text-[#008A99] uppercase tracking-[0.15em] mb-4">Product Divisions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {divisions.map((div) => (
            <div key={div.name} className="bg-white rounded-xl border border-gray-200/80 p-4 shadow-sm hover:border-[#008A99]/20 hover:shadow-md transition-all duration-300">
              <div className="flex items-center gap-2 mb-1.5">
                <Microscope className="w-4 h-4 text-[#008A99]" />
                <h4 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{div.name}</h4>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">{div.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Product Image */}
      <div className="relative h-40 rounded-2xl overflow-hidden" data-testid="about-product-image">
        <img src={PRODUCT_IMAGE} alt="Diagnostic Equipment" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/70 to-transparent" />
        <div className="absolute inset-0 flex items-center px-6">
          <div className="text-white">
            <p className="text-xs font-bold text-white/50 uppercase tracking-wider">State of the Art</p>
            <h3 className="text-lg font-bold mt-1" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Diagnostic Excellence</h3>
            <p className="text-xs text-white/60 mt-0.5">World-class IVD instruments and reagents</p>
          </div>
        </div>
      </div>

      {/* MyErba Program */}
      <div className="bg-gradient-to-br from-gray-900 to-[#008A99] rounded-2xl p-6 text-white shadow-xl" data-testid="about-program">
        <p className="text-xs font-bold text-white/40 uppercase tracking-[0.2em] mb-2">Loyalty Program</p>
        <h3 className="text-xl font-black" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>MyErba Rewards</h3>
        <p className="text-sm text-white/60 mt-2 leading-relaxed">
          Our loyalty rewards platform recognizes and rewards diagnostics professionals who choose TransAsia products.
          Scan QR codes on reagent kits, accumulate points, rise through tiers, and redeem exciting rewards.
        </p>
        <div className="flex gap-6 mt-5">
          <div>
            <p className="text-lg font-bold">3 Tiers</p>
            <p className="text-[10px] text-white/40 uppercase tracking-wider font-medium">Base, Gold, Platinum</p>
          </div>
          <div>
            <p className="text-lg font-bold">2% / 1%</p>
            <p className="text-[10px] text-white/40 uppercase tracking-wider font-medium">Points on Open / Closed</p>
          </div>
        </div>
      </div>

      {/* Powered By */}
      <div className="flex items-center justify-center gap-2 py-4 opacity-40">
        <span className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-medium">Powered by</span>
        <img src={TAGNPAY_LOGO} alt="TagNPay" className="h-4" />
      </div>
    </div>
  );
}
